import { render } from "preact";
import { FillsUpsellPopup } from "../components/cart-upsell/FillsUpsellPopup";
import { addVariantToCart } from "./cartApi";
import { readFillsUpsellConfig, type FillsUpsellConfig } from "./config";
import {
  isFillsUpsellEligible,
  prefetchFillsEligibility,
} from "./eligibility";
import {
  trackFillsSuggestionAccepted,
  trackFillsSuggestionDismissed,
  trackFillsSuggestionShown,
  type FillsSuggestionDismissMethod,
} from "./tracking";
import styles from "../index.css?inline";

const SEEN_KEY = "so_fills_upsell_seen";
const FORCE_PARAM = "so_fills_upsell";
const ENABLE_PARAM = "so_fills_upsell_enable";
const ENABLED_KEY = "so_fills_upsell_enabled";
const HOST_ID = "social-ocean-fills-upsell-host";
const EVENT_NAME = "social-ocean:before-add-to-cart";

/**
 * Shape of the CustomEvent detail dispatched by the theme's ajaxCart
 * formOverride (store-theme assets/theme.js). `proceed` performs the original
 * add exactly as if no interception happened; `openCart` reloads and opens
 * the cart drawer after the widget performed its own add.
 */
export interface BeforeAddToCartDetail {
  variantId: number | null;
  quantity: number;
  sellingPlanId: string | null;
  proceed: () => void;
  openCart: () => void;
}

function hasBeenShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return true; // storage unavailable → never intercept
  }
}

function markShownThisSession(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
}

/**
 * QA/preview override: `?so_fills_upsell=1` (or `=true`) forces the popup on for
 * this page, bypassing the soft-launch gate, the prior-purchase eligibility
 * gate, and the once-per-session lock. The variant and selling-plan gates still
 * apply, so it only fires on a one-time Rose Mist 2oz add. Real shoppers never
 * carry the param, so this can't affect production traffic. Mirrors the
 * `?so_cancel_chat` override on the cancellation chatbot.
 */
function isFillsUpsellForced(): boolean {
  try {
    const value = new URLSearchParams(window.location.search).get(FORCE_PARAM);
    return value === "1" || value === "true";
  } catch {
    return false;
  }
}

/**
 * Soft-launch gate for internal testing on the live storefront. The feature is
 * dark for everyone until `?so_fills_upsell_enable=1` (or `=true`) is visited
 * once on this browser; the choice persists in localStorage so a tester can
 * then browse and add to cart normally without re-appending the param.
 * `?so_fills_upsell_enable=0` clears it. Launching to all eligible buyers later
 * is a one-line change (default this to `true` / drop the gate in
 * `shouldIntercept`).
 */
function isFillsUpsellEnabled(): boolean {
  try {
    const value = new URLSearchParams(window.location.search).get(ENABLE_PARAM);
    if (value === "1" || value === "true") {
      localStorage.setItem(ENABLED_KEY, "1");
      return true;
    }
    if (value === "0" || value === "false") {
      localStorage.removeItem(ENABLED_KEY);
      return false;
    }
    return localStorage.getItem(ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

function shouldIntercept(
  detail: BeforeAddToCartDetail,
  config: FillsUpsellConfig,
): boolean {
  if (detail.variantId !== config.roseMistVariantId) return false;
  if (detail.sellingPlanId) return false;
  // The forced override skips every gate below so QA can re-test the popup
  // repeatedly on the live storefront without a seeded account.
  if (isFillsUpsellForced()) return true;
  // Soft-launch gate: dark for everyone until enabled on this browser.
  if (!isFillsUpsellEnabled()) return false;
  if (hasBeenShownThisSession()) return false;
  return isFillsUpsellEligible();
}

function showPopup(
  config: FillsUpsellConfig,
  detail: BeforeAddToCartDetail,
): void {
  document.getElementById(HOST_ID)?.remove();

  const host = document.createElement("div");
  host.id = HOST_ID;
  // Layer above the main widget host (z-index 99): sits in its own stacking
  // context via `isolation: isolate` on `so-widget-root` below, so the
  // popup's inner tw:z-9999 needs the host itself elevated too.
  host.style.position = "fixed";
  host.style.top = "0";
  host.style.left = "0";
  host.style.width = "0";
  host.style.height = "0";
  host.style.zIndex = "9999";
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });
  const root = document.createElement("div");
  root.className = "so-widget-root";
  root.style.isolation = "isolate";
  shadow.appendChild(root);
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styles);
    shadow.adoptedStyleSheets = [sheet];
  } catch {
    const style = document.createElement("style");
    style.textContent = styles;
    shadow.appendChild(style);
  }

  const eventData = {
    originalVariantId: config.roseMistVariantId,
    suggestedVariantId: config.fourFillsVariantId,
    quantity: detail.quantity,
  };

  const close = () => {
    render(null, root);
    host.remove();
  };

  const dismiss = (method: FillsSuggestionDismissMethod) => {
    trackFillsSuggestionDismissed({ ...eventData, method });
    close();
    detail.proceed();
  };

  render(
    <FillsUpsellPopup
      quantity={detail.quantity}
      roseMistPriceCents={config.roseMistPriceCents}
      fourFillsPriceCents={config.fourFillsPriceCents}
      onAccept={async () => {
        try {
          await addVariantToCart(config.fourFillsVariantId, detail.quantity);
        } catch {
          // Fetch itself failed: nothing was added, fall back to the
          // customer's original item.
          close();
          detail.proceed();
          return;
        }
        // The add succeeded — the item is in the cart. From here on we must
        // never call proceed(), or the customer gets both items.
        trackFillsSuggestionAccepted(eventData);
        close();
        try {
          detail.openCart();
        } catch {
          // Drawer refresh failed; the item is still in the cart.
        }
      }}
      onKeepMist={() => dismiss("keep_mist")}
      onClose={() => dismiss("close")}
    />,
    root,
  );
}

export function initFillsUpsell(): void {
  if (typeof window === "undefined") return;

  const initialConfig = readFillsUpsellConfig();
  // Config only exists on Mist product pages — also our prefetch guard.
  if (!initialConfig) return;

  // While the feature is dark (soft-launch gate off) we make no eligibility
  // call at all, so an un-enabled storefront has zero network footprint.
  if (isFillsUpsellEnabled()) {
    prefetchFillsEligibility();
  }

  window.addEventListener(EVENT_NAME, ((
    e: CustomEvent<BeforeAddToCartDetail>,
  ) => {
    // Hoisted so the catch block below can recover the add even if the
    // error happens after preventDefault() has already been called.
    let detail: BeforeAddToCartDetail | undefined;
    try {
      detail = e.detail;
      if (
        !detail ||
        typeof detail.proceed !== "function" ||
        typeof detail.openCart !== "function"
      ) {
        return;
      }
      // Re-read at dispatch time rather than closing over `initialConfig`:
      // the config block is only ever present on Mist PDPs, so this also
      // guards against a stale closure if the page navigated away.
      const config = readFillsUpsellConfig();
      if (!config) return;
      if (!shouldIntercept(detail, config)) return;
      e.preventDefault();
      markShownThisSession();
      trackFillsSuggestionShown({
        originalVariantId: config.roseMistVariantId,
        suggestedVariantId: config.fourFillsVariantId,
        quantity: detail.quantity,
      });
      showPopup(config, detail);
    } catch {
      // Never break add-to-cart. If we haven't prevented the default yet,
      // the theme's own handler still runs and nothing further is needed.
      // If we already called preventDefault() (e.g. showPopup threw after
      // it), the event is stuck canceled — tear down any partial popup and
      // proceed with the original add ourselves.
      if (e.defaultPrevented) {
        document.getElementById(HOST_ID)?.remove();
        try {
          detail?.proceed();
        } catch {
          /* nothing more we can do */
        }
      }
    }
  }) as EventListener);
}
